import { analytics } from "@/services/analytics.service";
import type { QueryClient } from "@tanstack/react-query";
import { API_CONFIG } from "@/lib/api/config";
import { getAuthorizedCachedUserLocation } from "@/hooks/useUserLocation";
import { authService } from "@/services/auth.service";
import { chatService } from "@/services/chat.service";
import { createStore, get, update } from "idb-keyval";
const apiOrigin = API_CONFIG.BASE_URL;
const getSession = () => {
  const user = authService.getCurrentUser();
  return user ? { user } : null;
};
const streamChatRequest = chatService.stream.bind(chatService);
import {
  applyChatEvent,
  chatHistory,
  chatRequestContext,
  chatFailureMessage,
  chatConversationUnchanged,
  ChatArchiveConflict,
  consumeChatStream,
  mergeChatConversation,
  readChatArchive,
  type ChatArchive,
  type ChatConversation,
  type ChatTurn,
  readChatOpeners,
  readChatOperations,
  mergeChatOperation,
  chatReferenceKey,
  chatOperationSummary,
  chatOperationTitle,
  chatRecoveryKind,
  type ChatItemReference,
  type ChatOpener,
  type ChatOpeners,
  type ChatOperation,
  type ChatOperationRequest,
} from "./chat-model";
import { refreshChatLibrary } from "./chat-library-effects";
import { readLegacyChats } from "./chat-legacy";
import { syncChatDocuments } from "./chat-sync";

type Snapshot = {
  loaded: boolean;
  conversations: ChatConversation[];
  readError?: string;
  saveError?: string;
  syncError?: string;
  syncNotice?: string;
  legacyImportError?: string;
  welcome?: ChatOpeners;
  welcomeLoading: boolean;
  welcomeError?: string;
  libraryRefreshError?: string;
};
type WelcomeCache = {
  openers: ChatOpeners;
  date: string;
  zone: string;
  daypart: number;
  recentIds: string[];
  sensitive: boolean;
  place?: string;
};
type WelcomeRecord = { generation?: string; cache?: WelcomeCache; displayedRelease?: string };
// Conversation documents are the person's only transcript copy. They never enter
// the bounded account/item cache, and ordinary sign-out does not delete them.
const disk = createStore("stasht-chat", "documents");
const stores = new Map<string, ChatStore>();
class ChatStore {
  private state: Snapshot = { loaded: false, conversations: [], welcomeLoading: false };
  private listeners = new Set<() => void>();
  private loading: Promise<void> | undefined;
  private writing: Promise<void> = Promise.resolve();
  private pending = new Map<string, ChatConversation | null>();
  private saved = new Map<string, ChatConversation>();
  private archiveGeneration: string | undefined;
  private clearing = false;
  private cleared = false;
  private cleanup: Promise<void> | undefined;
  private draftTimer: ReturnType<typeof setTimeout> | undefined;
  private syncing?: Promise<number>;
  private syncRequest?: AbortController;
  private measuredReplies = new Set<string>();
  private measureReply(
    requestId: string,
    outcome: "completed" | "failed",
    startedAt: number,
    reason?: "terminal_interrupted",
  ) {
    if (!this.currentAccount) return;
    const key = `${requestId}:${outcome}`;
    if (this.measuredReplies.has(key)) return;
    this.measuredReplies.add(key);
    analytics.capture(`chat_response_${outcome}`, {
      request_id: requestId,
      elapsed_ms: Math.max(0, Date.now() - startedAt * 1000),
      ...(reason ? { reason } : {}),
    });
  }
  private tasks = new Map<string, AbortController>();
  private operationCheckFailures = new Map<string, number>();
  private welcomeRequest?: AbortController;
  private welcomeCache?: WelcomeCache;
  private welcomeRestored = false;
  private welcomeGeneration?: string;
  private displayedRelease?: string;
  private welcomeWriting: Promise<void> = Promise.resolve();
  private actionConsumers = 0;
  constructor(
    readonly accountId: string,
    private key: string,
    readonly queryClient: QueryClient,
  ) {
    void this.restore();
  }
  snapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private publish(change: Partial<Snapshot>) {
    this.state = { ...this.state, ...change };
    this.listeners.forEach(listener => listener());
  }
  private change(id: string, update: (conversation: ChatConversation) => ChatConversation) {
    if (this.clearing) return;
    this.publish({
      conversations: this.state.conversations.map(conversation => {
        if (conversation.id !== id) return conversation;
        const next = update(conversation);
        this.pending.set(id, next);
        return next;
      }),
    });
  }
  private find(id: string) {
    return this.state.conversations.find(conversation => conversation.id === id);
  }
  get canSend() {
    return (
      !this.clearing && this.state.loaded && !this.state.readError && getSession()?.user.user_id === this.accountId
    );
  }
  get archiveCleared() {
    return this.cleared;
  }

  restore = async () => {
    if (this.clearing) return;
    if (this.loading) return this.loading;
    this.loading = (async () => {
      try {
        let archive = readChatArchive(await get<unknown>(this.key, disk));
        if (!this.currentAccount) return;
        let legacyImportError: string | undefined;
        try {
          // A deletion generation deliberately prevents retained old storage
          // from repopulating an account that was explicitly erased.
          const raw =
            !archive.generation && typeof localStorage !== "undefined"
              ? localStorage.getItem(`stashtchat:v1:${this.accountId}`)
              : null;
          if (raw !== null) {
            const legacy = readLegacyChats(raw, archive.legacyImportedIds || []);
            if (legacy.incomplete)
              legacyImportError =
                "Some older chats could not be imported. Their originals are unchanged in this browser.";
            if (legacy.conversations.length) {
              const write = this.writing
                .catch(() => undefined)
                .then(async () => {
                  this.checkAccount();
                  let committed = archive;
                  await update<ChatArchive>(
                    this.key,
                    stored => {
                      this.checkAccount();
                      const current = readChatArchive(stored, false);
                      if (current.generation) {
                        committed = current;
                        return current;
                      }
                      const imported = new Set(current.legacyImportedIds || []);
                      const conversations = new Map(
                        current.conversations.map(conversation => [conversation.id, conversation]),
                      );
                      for (const { sourceId, conversation } of legacy.conversations) {
                        if (!imported.has(sourceId)) {
                          if (!conversations.has(conversation.id)) conversations.set(conversation.id, conversation);
                          imported.add(sourceId);
                        }
                      }
                      committed = {
                        ...current,
                        legacyImportedIds: [...imported],
                        conversations: [...conversations.values()],
                      };
                      return committed;
                    },
                    disk,
                  );
                  archive = committed;
                });
              this.writing = write;
              await write;
              archive = readChatArchive(archive);
            }
          }
        } catch {
          legacyImportError =
            "Older chats could not be imported. Their originals are unchanged in this browser; reload this page to try again.";
        }
        if (!this.currentAccount) return;
        this.archiveGeneration = archive.generation;
        this.saved = new Map(archive.conversations.map(conversation => [conversation.id, conversation]));
        this.publish({ loaded: true, readError: undefined, legacyImportError, conversations: archive.conversations });
      } catch (error) {
        if (this.clearing) return;
        this.publish({
          loaded: true,
          readError:
            error instanceof Error
              ? error.message
              : "Saved chats could not be opened. They have been kept on this browser.",
        });
      }
    })().finally(() => {
      this.loading = undefined;
      // Sign-in can overlap the final microtask of a restore abandoned while
      // signed out. Loaded transcripts/drafts never need this retry.
      if (!this.state.loaded && this.currentAccount) void this.restore();
    });
    return this.loading;
  };
  save = async () => {
    if (this.clearing) return;
    if (!this.state.loaded || this.state.readError) throw new Error("Load your saved chats before making changes.");
    if (this.draftTimer) {
      clearTimeout(this.draftTimer);
      this.draftTimer = undefined;
    }
    if (!this.pending.size) return;
    // Serialize disk writes together with their baseline update. A queued save
    // takes its snapshot only after the preceding merge is reflected locally.
    const write = this.writing
      .catch(() => undefined)
      .then(async () => {
        if (this.clearing || !this.pending.size) return;
        const changes = new Map(this.pending),
          baseline = new Map(this.saved);
        const committed = new Map<string, ChatConversation | null>();
        await update<ChatArchive>(
          this.key,
          stored => {
            const current = readChatArchive(stored, false);
            if (current.generation !== this.archiveGeneration) throw new ChatArchiveConflict();
            const conversations = new Map(current.conversations.map(conversation => [conversation.id, conversation]));
            const deletions = new Set(current.deletions || []);
            for (const [id, conversation] of changes) {
              const base = baseline.get(id),
                stored = conversations.get(id);
              if (conversation) {
                const merged = mergeChatConversation(stored, base, conversation);
                conversations.set(id, merged);
                committed.set(id, merged);
              } else {
                if (stored && !chatConversationUnchanged(stored, base)) throw new ChatArchiveConflict();
                if (stored?.cloud || stored?.turns.length) deletions.add(id);
                conversations.delete(id);
                committed.set(id, null);
              }
            }
            return { ...current, conversations: [...conversations.values()], deletions: [...deletions] };
          },
          disk,
        );
        for (const [id, conversation] of changes) {
          if (conversation) this.saved.set(id, conversation);
          else this.saved.delete(id);
          if (this.pending.get(id) === conversation) {
            this.pending.delete(id);
            const merged = committed.get(id);
            if (merged && conversation) {
              // Only this page's own reply can drive an active spinner. A reply
              // recovered from another page has the same status as on restore.
              const local = {
                ...merged,
                turns: merged.turns.map(turn =>
                  turn.state === "answering" &&
                  !conversation.turns.some(value => value.id === turn.id && value.state === "answering")
                    ? { ...turn, state: "interrupted" as const }
                    : turn,
                ),
              };
              this.saved.set(id, local);
              this.publish({ conversations: this.state.conversations.map(value => (value.id === id ? local : value)) });
            }
          }
        }
        this.publish({ saveError: undefined });
      })
      .catch(error => {
        this.publish({
          saveError:
            error instanceof ChatArchiveConflict
              ? error.message
              : "Your latest chat changes could not be saved in this browser. Keep this page open and try saving again.",
        });
        throw error instanceof ChatArchiveConflict
          ? error
          : new Error("Your prompt could not be saved. Try saving again before sending.");
      });
    this.writing = write;
    return write;
  };
  private checkpoint() {
    void this.save().catch(() => undefined);
  }
  sync = (): Promise<number> => {
    if (this.syncing) return this.syncing;
    this.syncRequest = new AbortController();
    const signal = this.syncRequest.signal;
    this.syncing = (async () => {
      if (!this.state.loaded) await this.restore();
      this.checkAccount();
      await this.save();
      const baseline = this.state.conversations;
      const archive = readChatArchive(await get(this.key, disk));
      const result = await syncChatDocuments(baseline, archive.deletions || [], new Set(this.tasks.keys()), signal);
      this.checkAccount();
      signal.throwIfAborted();
      let keptDraft = false;
      for (const id of result.deleted) {
        const current = this.find(id);
        if (!current) continue;
        this.tasks.get(id)?.abort();
        this.tasks.delete(id);
        // A device-local unsent message survives deletion of the shared chat.
        if (current.draft.trim() || current.draftSelection?.length) {
          this.createDraft(current.draft, current.draftSelection);
          keptDraft = true;
        }
        this.pending.set(id, null);
        this.publish({ conversations: this.state.conversations.filter(value => value.id !== id) });
      }
      for (const incoming of result.conversations) {
        const before = baseline.find(value => value.id === incoming.id),
          current = this.find(incoming.id);
        if (before && !current) continue; // Deleted here while the request was running.
        let merged = incoming;
        if (before && current) {
          merged = mergeChatConversation(incoming, before, current);
          merged.turns = merged.turns.map(turn => ({
            ...turn,
            cloud: incoming.turns.find(value => value.id === turn.id)?.cloud ?? turn.cloud,
          }));
        }
        if (JSON.stringify(merged) === JSON.stringify(current)) continue;
        this.pending.set(merged.id, merged);
        this.publish({
          conversations: current
            ? this.state.conversations.map(value => (value.id === merged.id ? merged : value))
            : [...this.state.conversations, merged],
        });
      }
      await this.save();
      if (result.deleted.length) {
        const acknowledged = new Set(result.deleted);
        const write = this.writing.then(() =>
          update<ChatArchive>(
            this.key,
            raw => {
              const current = readChatArchive(raw, false);
              if (current.generation !== this.archiveGeneration) throw new ChatArchiveConflict();
              return { ...current, deletions: current.deletions?.filter(id => !acknowledged.has(id)) };
            },
            disk,
          ),
        );
        this.writing = write;
        await write;
      }
      this.publish({
        syncError: undefined,
        syncNotice: keptDraft
          ? "A chat was deleted on another device. Your unsent draft is kept as a new chat."
          : result.notice,
      });
      return Date.now();
    })()
      .catch(error => {
        if (this.currentAccount && !signal.aborted)
          this.publish({ syncError: "Chats could not sync. Your local copy is kept; try again when connected." });
        throw error;
      })
      .finally(() => {
        this.syncing = undefined;
        this.syncRequest = undefined;
      });
    return this.syncing;
  };
  private requestSync() {
    void this.queryClient.invalidateQueries({ queryKey: ["chat-history", this.accountId] });
  }
  /** Only the full receipt UI opts this account store into library write tools. */
  enableLibraryActions() {
    this.actionConsumers++;
    return () => {
      this.actionConsumers = Math.max(0, this.actionConsumers - 1);
    };
  }
  private get currentAccount() {
    return !this.clearing && getSession()?.user.user_id === this.accountId;
  }
  private checkAccount() {
    if (!this.currentAccount) throw new DOMException("Account changed", "AbortError");
  }
  private async requestContext(libraryActions = false) {
    const context = chatRequestContext(undefined, undefined, libraryActions);
    const location = await getAuthorizedCachedUserLocation(15 * 60 * 1000);
    if (location)
      context.place = {
        latitude: Math.round(location.lat * 100) / 100,
        longitude: Math.round(location.lng * 100) / 100,
        observed_at: new Date(location.timestamp).toISOString(),
      };
    return context;
  }
  private publishWelcome() {
    const openers = this.welcomeCache?.openers;
    this.publish({
      welcome: openers
        ? {
            ...openers,
            release_offer: openers.release_offer?.id === this.displayedRelease ? undefined : openers.release_offer,
          }
        : undefined,
    });
  }
  private saveWelcome() {
    const record: WelcomeRecord = { cache: this.welcomeCache, displayedRelease: this.displayedRelease };
    const write = this.welcomeWriting
      .catch(() => undefined)
      .then(async () => {
        this.checkAccount();
        await update<WelcomeRecord>(
          this.key + ":welcome",
          old => {
            if (old?.generation !== this.welcomeGeneration) throw new ChatArchiveConflict();
            return {
              ...record,
              ...(old?.generation ? { generation: old.generation } : {}),
              displayedRelease: record.displayedRelease || old?.displayedRelease,
            };
          },
          disk,
        );
      });
    this.welcomeWriting = write;
    return write;
  }
  async prepareWelcome(options: { refresh?: boolean; recentIds?: string[]; showSensitive?: boolean } = {}) {
    if (!this.canSend || this.welcomeRequest) return;
    const controller = new AbortController();
    this.welcomeRequest = controller;
    this.publish({ welcomeLoading: true, welcomeError: undefined });
    try {
      if (!this.welcomeRestored) {
        const stored = await get<WelcomeRecord>(this.key + ":welcome", disk);
        this.checkAccount();
        controller.signal.throwIfAborted();
        if (stored?.cache) {
          try {
            this.welcomeCache = { ...stored.cache, openers: readChatOpeners(stored.cache.openers) };
          } catch {
            /* Disposable suggestions never replace the transcript. */
          }
        }
        this.displayedRelease = stored?.displayedRelease;
        this.welcomeGeneration = stored?.generation;
        this.welcomeRestored = true;
      }
      const context = await this.requestContext(),
        cache = this.welcomeCache;
      const recentIds = (options.recentIds || []).slice(0, 12),
        sensitive = options.showSensitive === true;
      const usable =
        cache &&
        Date.parse(cache.openers.expires_at) > Date.now() &&
        cache.date === context.request_date &&
        cache.zone === context.time_zone &&
        cache.daypart === context.daypart &&
        cache.sensitive === sensitive &&
        cache.place === JSON.stringify(context.place && [context.place.latitude, context.place.longitude]);
      if (!usable) this.welcomeCache = undefined;
      this.publishWelcome();
      const sameEvidence = JSON.stringify(cache?.recentIds) === JSON.stringify(recentIds);
      // A first save can replace help choices. Useful subjects stay stable until
      // an explicit refresh or their server expiry; typing never rotates them.
      const helpOnly =
        cache?.openers.prompts.length && cache.openers.prompts.every(prompt => prompt.id?.startsWith("help."));
      if (!options.refresh && usable && (sameEvidence || !helpOnly)) return;
      context.displayed_release_id = this.displayedRelease;
      await streamChatRequest({ mode: "openers", ask_context: context }, controller.signal, (response, signal) =>
        consumeChatStream(
          response,
          event => {
            this.checkAccount();
            signal.throwIfAborted();
            if (event.type === "error") throw new Error("Chat suggestions could not load. Try refreshing them.");
            if (event.type === "openers") {
              this.welcomeCache = {
                openers: readChatOpeners(event.data),
                date: context.request_date,
                zone: context.time_zone,
                daypart: context.daypart,
                recentIds,
                sensitive,
                place: JSON.stringify(context.place && [context.place.latitude, context.place.longitude]),
              };
              this.publishWelcome();
            }
          },
          signal,
          "openers",
        ),
      );
      await this.saveWelcome();
    } catch (error) {
      if (this.currentAccount && !controller.signal.aborted)
        this.publish({
          welcomeError:
            error instanceof Error ? error.message : "Chat suggestions could not load. Try refreshing them.",
        });
    } finally {
      if (this.welcomeRequest === controller) {
        this.welcomeRequest = undefined;
        this.publish({ welcomeLoading: false });
      }
    }
  }
  cancelWelcome() {
    const request = this.welcomeRequest;
    if (!request) return;
    // A new visible account/evidence scope can start immediately; the previous
    // request's callback and finally remain guarded by signal and identity.
    this.welcomeRequest = undefined;
    request.abort();
    this.publish({ welcomeLoading: false });
  }
  async acknowledgeWelcome(releaseId: string) {
    if (
      !this.currentAccount ||
      this.welcomeCache?.openers.release_offer?.id !== releaseId ||
      this.displayedRelease === releaseId
    )
      return;
    this.displayedRelease = releaseId;
    // Keep this already visible choice steady. Its next appearance omits it.
    try {
      await this.saveWelcome();
    } catch {
      if (this.currentAccount)
        this.publish({
          welcomeError: "Your displayed suggestion could not be remembered. Try refreshing suggestions.",
        });
    }
  }
  createDraft(text: string, refs: ChatItemReference[] = []) {
    if (!this.canSend || (!text.trim() && !refs.length)) return undefined;
    const conversation: ChatConversation = {
      id: crypto.randomUUID(),
      title: text.trim().slice(0, 80) || "New chat",
      createdAt: Date.now(),
      updatedAt: Date.now(),
      draft: text,
      ...(refs.length ? { draftSelection: refs } : {}),
      turns: [],
    };
    this.pending.set(conversation.id, conversation);
    this.publish({ conversations: [conversation, ...this.state.conversations] });
    this.checkpoint();
    return conversation.id;
  }
  toggleSelection(id: string, ref: ChatItemReference) {
    if (!this.canSend) return;
    this.change(id, conversation => {
      const selected = conversation.draftSelection || [],
        key = chatReferenceKey(ref);
      return {
        ...conversation,
        draftSelection: selected.some(value => chatReferenceKey(value) === key)
          ? selected.filter(value => chatReferenceKey(value) !== key)
          : [...selected, ref],
      };
    });
    this.checkpoint();
  }
  setDraft(id: string, text: string) {
    if (!this.canSend) return;
    if (!text.trim() && !this.find(id)?.turns.length && !this.find(id)?.draftSelection?.length) {
      void this.remove(id).catch(() => undefined);
      return;
    }
    this.change(id, conversation => ({
      ...conversation,
      draft: text,
      updatedAt: Date.now(),
      title:
        conversation.turns.length || conversation.titleWasEdited
          ? conversation.title
          : text.trim().slice(0, 80) || "New chat",
    }));
    if (this.draftTimer) clearTimeout(this.draftTimer);
    this.draftTimer = setTimeout(() => this.checkpoint(), 350);
  }
  async rename(id: string, title: string) {
    if (this.clearing || !title.trim() || this.state.readError) return;
    this.change(id, conversation => ({ ...conversation, title: title.trim().slice(0, 120), titleWasEdited: true }));
    await this.save();
    this.requestSync();
  }
  async remove(id: string) {
    const original = this.find(id);
    if (this.clearing || !original || this.state.readError) return;
    this.tasks.get(id)?.abort();
    this.tasks.delete(id);
    for (const operation of original.turns.flatMap(turn => turn.operations || []))
      this.operationCheckFailures.delete(operation.operation_id);
    const retained = {
      ...original,
      turns: original.turns.map(turn =>
        turn.state === "answering" ? { ...turn, state: "interrupted" as const } : turn,
      ),
    };
    this.pending.set(id, null);
    this.publish({ conversations: this.state.conversations.filter(conversation => conversation.id !== id) });
    try {
      await this.save();
      this.requestSync();
    } catch (error) {
      if (!this.clearing) {
        this.pending.set(id, retained);
        this.publish({ conversations: [...this.state.conversations, retained] });
      }
      throw error;
    }
  }
  stop(id: string) {
    this.tasks.get(id)?.abort();
    this.tasks.delete(id);
    this.change(id, conversation => ({
      ...conversation,
      turns: conversation.turns.map(turn => (turn.state === "answering" ? { ...turn, state: "stopped" } : turn)),
    }));
    this.checkpoint();
  }
  private receive(id: string, turnId: string, event: Parameters<typeof applyChatEvent>[1]) {
    this.change(id, conversation => {
      const turn = conversation.turns.find(value => value.id === turnId);
      if (!turn || turn.state !== "answering") throw new DOMException("Reply stopped", "AbortError");
      let next = applyChatEvent(turn, event);
      const incoming = event.type === "operations" || event.type === "complete" ? next.operations || [] : [];
      if (!incoming.length)
        return { ...conversation, turns: conversation.turns.map(value => (value.id === turnId ? next : value)) };
      for (const operation of incoming)
        if (operation.status !== "pending") this.operationCheckFailures.delete(operation.operation_id);
      const previous = conversation.turns.flatMap(value => value.operations || []);
      const resolved = incoming.map(value =>
        mergeChatOperation(
          previous.find(old => old.operation_id === value.operation_id && old.effect) ||
            previous.find(old => old.operation_id === value.operation_id),
          value,
        ),
      );
      const priorIDs = new Set(
        conversation.turns
          .filter(value => value.id !== turnId)
          .flatMap(value => value.operations || [])
          .map(value => value.operation_id),
      );
      const handled = new Set(
        (turn.selectedReferences || [])
          .filter(ref =>
            incoming.some(
              operation =>
                !priorIDs.has(operation.operation_id) &&
                !operation.action?.endsWith("_undo") &&
                operation.action !== "undo" &&
                (operation.items?.some(item => chatReferenceKey(item.ref) === chatReferenceKey(ref)) ||
                  operation.outcomes?.some(
                    outcome =>
                      outcome.key === `${ref.kind}#${ref.id}` ||
                      (ref.kind === "stash" && outcome.post_id === ref.id) ||
                      (ref.kind === "entity" && outcome.entity_id === ref.id),
                  )),
            ),
          )
          .map(chatReferenceKey),
      );
      next = { ...next, operations: resolved };
      return {
        ...conversation,
        draftSelection: conversation.draftSelection?.filter(ref => !handled.has(chatReferenceKey(ref))),
        turns: conversation.turns.map(value =>
          value.id === turnId
            ? next
            : {
                ...value,
                operations: value.operations?.map(
                  old => resolved.find(value => value.operation_id === old.operation_id) || old,
                ),
              },
        ),
      };
    });
  }
  private async refreshLibrary(operations: ChatOperation[]) {
    if (!this.currentAccount) return;
    try {
      if (await refreshChatLibrary(operations, this.accountId, this.queryClient)) {
        this.checkAccount();
        this.publish({ libraryRefreshError: undefined });
      }
    } catch {
      if (this.currentAccount)
        this.publish({
          libraryRefreshError: "Your changes were saved, but the library view could not refresh. Try refreshing again.",
        });
    }
  }
  async refreshChanges(id: string) {
    await this.refreshLibrary(this.find(id)?.turns.flatMap(turn => turn.operations || []) || []);
  }
  async send(
    id: string,
    retry = false,
    options: {
      opener?: ChatOpener;
      followUp?: string;
      operation?: ChatOperationRequest;
      operationTargetID?: string;
    } = {},
  ) {
    const conversation = this.find(id);
    if (!this.canSend || !conversation || this.tasks.has(id)) return;
    const previous = retry ? conversation.turns[conversation.turns.length - 1] : undefined;
    if (previous?.state === "complete") return;
    const query = (previous?.query ?? options.followUp ?? options.opener?.text ?? conversation.draft).trim();
    if (!query) return;
    const history = chatHistory(conversation, previous?.id),
      requestID =
        previous?.requestID ||
        (options.operation?.action === "undo" ? options.operation.request_id : crypto.randomUUID()),
      requestStartedAt =
        previous?.requestStartedAt ||
        (options.operation?.action === "undo" ? options.operation.request_started_at : Math.floor(Date.now() / 1000));
    const operationRequest = previous?.operationRequest || options.operation;
    const turn: ChatTurn = {
      id: crypto.randomUUID(),
      query,
      createdAt: Date.now(),
      state: "answering",
      text: "",
      sources: [],
      images: [],
      requestID,
      requestStartedAt,
      selectedReferences: operationRequest ? undefined : (previous?.selectedReferences ?? conversation.draftSelection),
      openerReferences: previous?.openerReferences ?? options.opener?.refs,
      openerAsk: previous?.openerAsk ?? options.opener?.ask,
      operationRequest,
      operationTargetID: previous?.operationTargetID ?? options.operationTargetID,
    };
    const controller = new AbortController();
    this.tasks.set(id, controller);
    this.change(id, current => ({
      ...current,
      title: current.turns.length || current.titleWasEdited ? current.title : query.slice(0, 80),
      draft: retry || options.followUp !== undefined || options.operation ? current.draft : "",
      updatedAt: Date.now(),
      turns: [
        ...current.turns
          .filter(
            value =>
              value.id !== previous?.id ||
              value.text ||
              value.sources.length ||
              value.images.length ||
              value.objects?.length ||
              value.operations?.length,
          )
          .map(value =>
            value.id === previous?.id
              ? {
                  ...value,
                  operations: value.operations?.map(operation =>
                    operation.status === "preview"
                      ? { ...operation, status: "superseded", operation_token: undefined }
                      : operation,
                  ),
                }
              : value,
          ),
        turn,
      ],
    }));
    let lastSave = Date.now();
    let replyRequested = false;
    try {
      await this.save();
      controller.signal.throwIfAborted();
      this.checkAccount();
      if (!this.canSend)
        throw new Error("Chat is not available for this account. Your saved conversations are still here.");
      if (operationRequest) {
        // Keep the action request available to another device before executing
        // it. Its existing operation receipt remains the authority for effects.
        if (this.syncing) await this.syncing;
        await this.sync();
        this.checkAccount();
        controller.signal.throwIfAborted();
        const response = await chatService.operations(operationRequest, controller.signal, () => this.currentAccount);
        this.checkAccount();
        controller.signal.throwIfAborted();
        const operations = readChatOperations(response.operations);
        if (
          operationRequest.action === "status" &&
          !operations.some(value => value.operation_id === operationRequest.operation_id)
        ) {
          const preview = this.find(id)
            ?.turns.flatMap(value => value.operations || [])
            .find(
              value =>
                value.operation_id === operationRequest.operation_id &&
                value.status === "preview" &&
                value.operation_token &&
                (value.expires_at || 0) > Date.now() / 1000,
            );
          const message = preview
            ? "The result could not be confirmed. You can still apply the original preview before it expires."
            : "The original result could not be found. Use Check result to try again, or inspect your library.";
          this.markOperationUnknown(id, operationRequest.operation_id, message);
          throw new Error(message);
        }
        for (const operation of operations) this.operationCheckFailures.delete(operation.operation_id);
        this.receive(id, turn.id, { type: "operations", data: { operations } });
        this.change(id, value => ({
          ...value,
          turns: value.turns.map(current =>
            current.id === turn.id
              ? {
                  ...current,
                  text:
                    operations.map(chatOperationSummary).join("\n\n") ||
                    "No recorded changes were found for this request.",
                  textSettled: true,
                  state: "complete",
                  operationRequest: undefined,
                }
              : current,
          ),
        }));
      } else {
        const context = await this.requestContext(this.actionConsumers > 0);
        context.request_id = requestID;
        context.request_started_at = requestStartedAt;
        context.conversation_id = id;
        context.turn_id = turn.id;
        context.opener_refs = turn.openerReferences;
        context.opener_ask = turn.openerAsk;
        context.opener_id = retry ? undefined : options.opener?.id;
        context.selected_refs = turn.selectedReferences;
        context.conversation_objects = conversation.turns
          .slice(-4)
          .flatMap(value => value.objects || [])
          .slice(-50);
        context.previous_operations = conversation.turns
          .slice(-4)
          .flatMap(value => value.operations || [])
          .slice(-12)
          .map(value => ({ ...value, operation_token: undefined }));
        replyRequested = true;
        await streamChatRequest(
          { message: query, conversation_history: history, ask_context: context },
          controller.signal,
          (response, signal) =>
            consumeChatStream(
              response,
              event => {
                signal.throwIfAborted();
                this.checkAccount();
                this.receive(id, turn.id, event);
                if (
                  Date.now() - lastSave >= 2000 ||
                  ["sources", "images", "objects", "operations", "answer_end", "complete"].includes(event.type)
                ) {
                  lastSave = Date.now();
                  this.checkpoint();
                }
              },
              signal,
            ),
        );
      }
      if (!operationRequest && !controller.signal.aborted) {
        const outcome = this.find(id)?.turns.find(value => value.id === turn.id)?.state;
        if (outcome === "complete") this.measureReply(requestID, "completed", requestStartedAt);
        if (outcome === "interrupted") this.measureReply(requestID, "failed", requestStartedAt, "terminal_interrupted");
      }
    } catch (error) {
      if (replyRequested && !controller.signal.aborted) this.measureReply(requestID, "failed", requestStartedAt);
      this.change(id, current => ({
        ...current,
        turns: current.turns.map(value =>
          value.id !== turn.id || value.state !== "answering"
            ? value
            : {
                ...value,
                state: value.text || controller.signal.aborted ? "interrupted" : "failed",
                error: chatFailureMessage(error),
              },
        ),
      }));
    } finally {
      if (this.tasks.get(id) === controller) this.tasks.delete(id);
      this.checkpoint();
      this.requestSync();
      await this.refreshLibrary(this.find(id)?.turns.find(value => value.id === turn.id)?.operations || []);
    }
  }
  async performOperation(
    id: string,
    operation: ChatOperation,
    action: "apply" | "resume" | "status" | "cancel" | "undo",
  ) {
    if (!this.actionConsumers || !this.canSend) return;
    const known = this.find(id)
      ?.turns.flatMap(turn => turn.operations || [])
      .find(value => value.operation_id === operation.operation_id);
    if (!known) return;
    let command: ChatOperationRequest;
    if (action === "apply") {
      if (known.status !== "preview" || !known.operation_token || (known.expires_at || 0) <= Date.now() / 1000) return;
      command = { action, operation_token: known.operation_token };
    } else if (action === "undo") {
      if (!known.undoable || known.status === "preview") return;
      command = {
        action,
        original_operation_id: known.operation_id,
        request_id: crypto.randomUUID(),
        request_started_at: Math.floor(Date.now() / 1000),
      };
    } else {
      if (action === "cancel" && !known.cancellable) return;
      command = { action, operation_id: known.operation_id };
    }
    const verb = { apply: "Apply", resume: "Continue", status: "Check", cancel: "Stop", undo: "Undo" }[action];
    await this.send(id, false, {
      followUp: `${verb}: ${chatOperationTitle(known)}`,
      operation: command,
      operationTargetID: known.operation_id,
    });
  }
  async recoverRequest(id: string, turnId: string) {
    const conversation = this.find(id),
      turn = conversation?.turns.find(value => value.id === turnId);
    if (!this.actionConsumers || !turn?.requestID || turn.state === "answering") return;
    const command = turn.operationRequest;
    let recovery: ChatOperationRequest = { action: "recover", request_id: turn.requestID };
    // A UI receipt turn is not the server operation owner. Preserve the exact
    // target through lost responses and through failures of recovery itself.
    if (command)
      switch (command.action) {
        case "apply": {
          const preview = conversation?.turns
            .flatMap(value => value.operations || [])
            .find(value => value.operation_token === command.operation_token);
          const target = turn.operationTargetID || preview?.operation_id;
          if (!target) {
            this.change(id, value => ({
              ...value,
              turns: value.turns.map(item =>
                item.id === turnId
                  ? {
                      ...item,
                      error: "The original change could not be identified. Check your library before trying again.",
                    }
                  : item,
              ),
            }));
            this.checkpoint();
            return;
          }
          recovery = { action: "status", operation_id: target };
          break;
        }
        case "resume":
        case "status":
        case "cancel":
          recovery = { action: "status", operation_id: command.operation_id };
          break;
        case "undo":
        case "recover":
          recovery = { action: "recover", request_id: command.request_id };
          break;
      }
    await this.send(id, false, {
      followUp:
        chatRecoveryKind(turn) === "status"
          ? "Check the result of my request"
          : "Recover my original request and continue unfinished changes",
      operation: recovery,
    });
  }
  dismissOperation(id: string, operationId: string) {
    this.change(id, conversation => ({
      ...conversation,
      turns: conversation.turns.map(turn => ({
        ...turn,
        operations: turn.operations?.map(operation =>
          operation.operation_id === operationId && operation.status === "preview"
            ? { ...operation, status: "superseded", operation_token: undefined }
            : operation,
        ),
      })),
    }));
    this.checkpoint();
  }
  private markOperationUnknown(id: string, operationId: string, message: string) {
    this.operationCheckFailures.delete(operationId);
    // This is local uncertainty, not a server failure/completion claim. Keep all
    // previously confirmed outcomes and context; a later receipt replaces it.
    // Stateless signed previews stay available for an explicit Apply until expiry.
    this.change(id, conversation => ({
      ...conversation,
      turns: conversation.turns.map(turn => ({
        ...turn,
        operations: turn.operations?.map(operation =>
          operation.operation_id === operationId && ["pending", "unknown"].includes(operation.status)
            ? { ...operation, status: "unknown", operation_token: undefined, error: message }
            : operation,
        ),
      })),
    }));
  }
  async followOperations(id: string, signal: AbortSignal) {
    // A successful explicit receipt check resets its own budget even while
    // another operation keeps this follower alive. Unrelated receipts cannot.
    const failures = this.operationCheckFailures;
    const pendingOperations = () => [
      ...new Map(
        (this.find(id)?.turns.flatMap(turn => turn.operations || []) || [])
          .filter(operation => operation.background && operation.status === "pending")
          .map(operation => [operation.operation_id, operation]),
      ).values(),
    ];
    const stopForSaveFailure = () => {
      if (signal.aborted || !this.currentAccount) return;
      // No receipt can still appear to be followed after this loop exits.
      // Retain already confirmed terminal results; only pending work is unknown.
      for (const operation of pendingOperations())
        this.markOperationUnknown(
          id,
          operation.operation_id,
          "Automatic checks stopped because the latest result could not be saved in this browser. Keep this page open and resolve the save error before checking again.",
        );
    };
    const pause = async (operationId: string, message: string) => {
      this.markOperationUnknown(id, operationId, message);
      try {
        await this.save();
        return true;
      } catch {
        stopForSaveFailure();
        return false;
      } // saveError remains visible.
    };
    while (!signal.aborted && this.currentAccount) {
      const pending = pendingOperations();
      if (!pending.length) return;
      let delay = 3000;
      for (const operation of pending) {
        if (signal.aborted || !this.currentAccount) return;
        let incoming: ChatOperation[];
        try {
          const response = await chatService.operations(
            { action: "status", operation_id: operation.operation_id },
            signal,
            () => this.currentAccount,
          );
          signal.throwIfAborted();
          this.checkAccount();
          incoming = readChatOperations(response.operations);
        } catch {
          if (signal.aborted || !this.currentAccount) return;
          const count = (failures.get(operation.operation_id) || 0) + 1;
          failures.set(operation.operation_id, count);
          if (count < 3) delay = Math.max(delay, 3000 * 2 ** count);
          else if (
            !(await pause(
              operation.operation_id,
              "The latest result could not be checked. Automatic checks have stopped. Use Check result to try again.",
            ))
          )
            return;
          continue;
        }
        if (!incoming.some(value => value.operation_id === operation.operation_id)) {
          if (
            !(await pause(
              operation.operation_id,
              "The original result could not be found. Use Check result to try again, or inspect your library.",
            ))
          )
            return;
          continue;
        }
        failures.delete(operation.operation_id);
        this.change(id, conversation => ({
          ...conversation,
          turns: conversation.turns.map(turn => ({
            ...turn,
            operations: turn.operations?.map(old => {
              const newer = incoming.find(value => value.operation_id === old.operation_id);
              return newer ? mergeChatOperation(old, newer) : old;
            }),
          })),
        }));
        try {
          await this.save();
        } catch {
          stopForSaveFailure();
          return;
        }
        await this.refreshLibrary(incoming.filter(value => !(value.background && value.status === "pending")));
      }
      if (signal.aborted || !this.currentAccount || !pendingOperations().length) return;
      await new Promise<void>(resolve => {
        const done = () => {
          clearTimeout(timer);
          signal.removeEventListener("abort", done);
          resolve();
        };
        const timer = setTimeout(done, delay);
        signal.addEventListener("abort", done, { once: true });
      });
    }
  }
  leaveAccount() {
    this.syncRequest?.abort();
    if (this.clearing) return;
    this.cancelWelcome();
    this.actionConsumers = 0;
    this.operationCheckFailures.clear();
    for (const task of this.tasks.values()) task.abort();
    this.tasks.clear();
    if (!this.state.loaded || this.state.readError) return;
    for (const conversation of this.state.conversations)
      if (conversation.turns.some(turn => turn.state === "answering")) {
        this.change(conversation.id, value => ({
          ...value,
          turns: value.turns.map(turn => (turn.state === "answering" ? { ...turn, state: "interrupted" } : turn)),
        }));
      }
    this.checkpoint();
  }
  /** Only called after a confirmed account-deletion receipt. Stop producers,
   * settle queued writes, then erase transcript contents. A fresh archive
   * generation rejects stale writers from other tabs after this transaction. */
  clearForAccountDeletion = async () => {
    if (this.cleared) return;
    if (this.cleanup) return this.cleanup;
    this.clearing = true;
    this.syncRequest?.abort();
    this.cancelWelcome();
    if (this.draftTimer) {
      clearTimeout(this.draftTimer);
      this.draftTimer = undefined;
    }
    for (const task of this.tasks.values()) task.abort();
    this.tasks.clear();
    this.operationCheckFailures.clear();
    this.pending.clear();
    this.publish({});
    this.cleanup = (async () => {
      await this.writing.catch(() => undefined);
      await this.welcomeWriting.catch(() => undefined);
      const generation = crypto.randomUUID();
      await update<WelcomeRecord>(this.key + ":welcome", () => ({ generation }), disk);
      this.welcomeGeneration = generation;
      this.welcomeCache = undefined;
      this.displayedRelease = undefined;
      this.publishWelcome();
      await update<ChatArchive>(this.key, () => ({ version: 1, generation, conversations: [] }), disk);
      this.archiveGeneration = generation;
      this.cleared = true;
      this.saved.clear();
      this.publish({ loaded: true, conversations: [], readError: undefined, saveError: undefined });
    })().finally(() => {
      this.cleanup = undefined;
    });
    return this.cleanup;
  };
}
export function chatStore(accountId: string, queryClient: QueryClient) {
  const key = JSON.stringify([apiOrigin, accountId]);
  if (!stores.has(key)) stores.set(key, new ChatStore(accountId, key, queryClient));
  return stores.get(key)!;
}
export function leaveChatAccount(accountId: string) {
  for (const store of stores.values()) if (store.accountId === accountId) store.leaveAccount();
}
export async function clearAccountChats(accountId: string) {
  const store = [...stores.values()].find(value => value.accountId === accountId);
  if (store) await store.clearForAccountDeletion();
  else {
    const key = JSON.stringify([apiOrigin, accountId]),
      generation = crypto.randomUUID();
    await update(key + ":welcome", () => ({ generation }), disk);
    await update(key, () => ({ version: 1, generation, conversations: [] }), disk);
  }
  localStorage.removeItem(`stashtchat:v1:${accountId}`);
}
